import React, { useState } from "react";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import { ChevronRight, Phone, Mail, MapPin, Clock, Send, MessageCircle, Loader2 } from "lucide-react";
import { contact } from "../mock";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";
import { submitContactInquiry } from "../lib/api";
import useSeo from "../hooks/useSeo";

export default function Contact() {
  useSeo({
    title: "Contact Us",
    description: "Get in touch with Ess Bee Heaters Pvt. Ltd. Call +91-9911565091 or send an inquiry online — we respond within 24 hours. Custom IR heating solutions available.",
    keywords: "contact Ess Bee Heaters, IR heater inquiry, heater quote India, industrial heater supplier contact",
    path: "/contact",
  });
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", phone: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.message) {
      toast({ title: "Missing fields", description: "Please fill all required fields." });
      return;
    }
    try {
      setLoading(true);
      await submitContactInquiry(form);
      toast({ title: "Inquiry sent", description: "We'll get back to you within 24 hours." });
      setForm({ name: "", phone: "", email: "", company: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg).join(", ") : (detail || err.message || "Please try again later.");
      toast({ title: "Submission failed", description: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <PageHero
        tagline="Get In Touch"
        title="Contact"
        highlight="Us"
        subtitle="Tell us your requirements and we'll get back within 24 hours with a tailored quote."
        crumb={(
          <div className="flex items-center gap-2">
            <Link to="/" className="hover:text-orange-500">Home</Link>
            <ChevronRight size={14} className="text-orange-500" />
            <span className="text-orange-500 font-semibold">Contact</span>
          </div>
        )}
      />

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-8">
          <form onSubmit={submit} className="lg:col-span-2 bg-white border border-neutral-200 rounded-2xl shadow-sm p-7 md:p-9">
            <p className="text-[11px] tracking-[0.3em] text-orange-600 font-semibold uppercase">Send a Message</p>
            <h3 className="font-display text-2xl md:text-3xl uppercase mt-2">Inquiry Form</h3>
            <span className="block w-16 h-[3px] bg-orange-500 mt-3 mb-6" />
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Full Name *"><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="John Doe" className="h-12" /></Field>
              <Field label="Phone *"><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+91 9876543210" className="h-12" /></Field>
              <Field label="Email *"><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="you@company.com" className="h-12" /></Field>
              <Field label="Company"><Input value={form.company} onChange={e => setForm({...form, company: e.target.value})} placeholder="Your company" className="h-12" /></Field>
            </div>
            <div className="mt-4">
              <Field label="Message *"><Textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} placeholder="Tell us about your heating requirements..." className="min-h-[140px]" /></Field>
            </div>
            <Button type="submit" disabled={loading} className="mt-6 w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-70 text-white rounded-full h-12 uppercase tracking-wider font-semibold">
              {loading ? (<><Loader2 size={16} className="mr-2 animate-spin" /> Sending...</>) : (<><Send size={16} className="mr-2" /> Send Inquiry</>)}
            </Button>
          </form>

          <div className="space-y-4">
            <InfoCard icon={Phone} title="Phone" lines={[<a key="p" href={`tel:${contact.phone}`} className="text-orange-500 font-semibold hover:text-orange-600">{contact.phone}</a>, "Mon-Sat, 9:00 AM - 6:00 PM"]} />
            <InfoCard icon={Mail} title="Email" lines={[<a key="e1" href={`mailto:${contact.emails[0]}`} className="text-orange-500 font-semibold hover:text-orange-600 break-all">{contact.emails[0]}</a>, contact.emails[1]]} />
            <InfoCard icon={MapPin} title="Location" lines={[contact.location, "Manufacturing facility"]} />
            <InfoCard icon={Clock} title="Hours" lines={["Mon - Sat: 9 AM - 6 PM", "Sunday: Closed"]} />

            <div className="bg-green-50 border-2 border-green-200 rounded-xl p-5 text-center">
              <p className="font-display text-lg uppercase">Need Instant Help?</p>
              <p className="text-sm text-neutral-600 mt-1">Chat with us on WhatsApp.</p>
              <a href={`https://wa.me/${contact.whatsapp}`} target="_blank" rel="noreferrer">
                <Button className="mt-4 w-full bg-green-500 hover:bg-green-600 text-white rounded-full h-11 uppercase tracking-wider font-semibold">
                  <MessageCircle size={16} className="mr-2" /> Chat on WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-wider text-neutral-700 mb-2">{label}</span>
      {children}
    </label>
  );
}

function InfoCard({ icon: Icon, title, lines }) {
  return (
    <div className="flex items-start gap-4 p-5 rounded-xl border border-neutral-200 hover:border-orange-300 hover:bg-cream transition-colors">
      <div className="w-12 h-12 shrink-0 rounded-xl bg-orange-500 text-white flex items-center justify-center">
        <Icon size={20} />
      </div>
      <div>
        <p className="font-display text-base uppercase">{title}</p>
        <div className="mt-1 space-y-0.5 text-sm text-neutral-600">
          {lines.map((l, i) => <div key={i}>{l}</div>)}
        </div>
      </div>
    </div>
  );
}
